import { type RecordId } from 'javascripts/shared-types';
import ApplicationModel, { attr } from '../ApplicationModel';
import ProjectFilter from '../filters/ProjectFilter';
import type RecordFilter from '../filters/RecordFilter';
import RecordTypeFilter from '../filters/RecordTypeFilter';
import ReleaseFilter from '../filters/ReleaseFilter';
import TagFilter from '../filters/TagFilter';
import TeamFilter from '../filters/TeamFilter';
import { model } from '../modelBuilder';
import type Query from '../Query';
import BookmarksRecordPosition from './BookmarksRecordPosition';
import BookmarksTeamBacklog from './BookmarksTeamBacklog';
import CardLayout from './CardLayout';
import { HasBookmarkFilters } from './concerns/HasBookmarkFilters';
import { HasBookmarkSidebarSettings } from './concerns/HasBookmarkSidebarSettings';
import Epic from './Epic';
import Feature from './Feature';
import { type BookmarksProgramBoardAttributes } from './generated/BookmarksProgramBoardAttributes';
import Project from './Project';
import ProjectConfiguration from './ProjectConfiguration';
import RecordLink from './RecordLink';
import Release from './Release';
import Requirement from './Requirement';
import Tag from './Tag';
import User from './User';
import WorkflowStatus from './WorkflowStatus';

export const BACKLOG_RELEASE_SCOPE = [
  'id',
  'name',
  'releaseDate',
  'parkingLot',
  'position',
];

const COMMON_FIELDS = ['id', 'referenceNum', 'name', 'path'];

interface BookmarksProgramBoard
  extends BookmarksProgramBoardAttributes,
    HasBookmarkFilters<{
      tags: TagFilter;
      recordTypes: RecordTypeFilter;
      team: TeamFilter;
      projects: ProjectFilter;
      releases: ReleaseFilter;
    }>,
    HasBookmarkSidebarSettings {}

@model
@HasBookmarkFilters
@HasBookmarkSidebarSettings
class BookmarksProgramBoard extends ApplicationModel {
  static OBJECT_CLASSES = [Feature, Requirement, Epic];

  // Start auto-generated
  static typename: 'BookmarksProgramBoard' = 'BookmarksProgramBoard';
  get typename(): 'BookmarksProgramBoard' {
    return 'BookmarksProgramBoard';
  }

  static internalTypename: 'Bookmarks::ProgramBoard' =
    'Bookmarks::ProgramBoard';
  get internalTypename(): 'Bookmarks::ProgramBoard' {
    return 'Bookmarks::ProgramBoard';
  }

  static bookmarkName = 'Bookmarks::ProgramBoard';

  static inputType = 'ProgramBoardAttributes!';

  static fields = {
    filters: attr({ writable: true, readable: true, json: true }),
    id: attr({ readable: true }),
    iterationsCount: attr({ readable: true }),
    programIncrementId: attr({ writable: true, readable: true }),
    projectId: attr({ readable: true }),
    showCollapsedView: attr({ writable: true, readable: true }),
    showDependencyLines: attr({ writable: true, readable: true }),
    sidebarSettings: attr({ writable: true, readable: true, json: true }),
    cardLayout: attr.belongsTo({
      writeShape: [
        'epicRecordLayout',
        'featureRecordLayout',
        'ideaRecordLayout',
        'initiativeRecordLayout',
        'releaseRecordLayout',
        'releasePhaseRecordLayout',
        'requirementRecordLayout',
        'taskRecordLayout',
        'goalRecordLayout',
        'keyResultRecordLayout',
      ],
      readable: true,
      typename: 'CardLayout',
    }),
    defaultCardLayout: attr.belongsTo({
      readable: true,
      typename: 'CardLayout',
    }),
    programIncrement: attr.belongsTo({
      readable: true,
      typename: 'ProgramIncrement',
    }),
    project: attr.belongsTo({ readable: true, typename: 'Project' }),
    iterations: attr.hasMany({ readable: true }),
  };
  // End auto-generated

  initialize() {
    this.local = {
      workflow: null,
      users: [],
      epics: [],
      features: [],
      filterObjects: {},
      unfilteredRecordIds: new Set(),
      records: [],
    };
  }

  static buildProgramBoardScope(loadCardLayout = false) {
    let scope = this.select([
      'id',
      'projectId',
      'programIncrementId',
      'showCollapsedView',
      'showDependencyLines',
      'filters',
      'sidebarSettings',
    ]).merge({
      iterations: ['id', 'name', 'status'],
      project: Project.select([
        'id',
        'backlogManagementEnabled',
        'iterationsEnabled',
        'referencePrefix',
      ]),
    });

    if (loadCardLayout) {
      scope = scope.merge({
        cardLayout: CardLayout.buildCardLayoutScope({
          recordTypes: ['Epic', 'Feature', 'Requirement'],
        }),
        defaultCardLayout: CardLayout.buildCardLayoutScope({
          recordTypes: ['Epic', 'Feature', 'Requirement'],
        }),
      });
    }

    return scope;
  }

  get defaultUser() {
    return User.blank({
      name: 'Unassigned',
    });
  }

  async loadRecords() {
    const records = await Promise.all(
      [Feature, Requirement, Epic].map(objectClass => {
        return BookmarksProgramBoard.buildRecordScope(objectClass, {
          cardLayout: this.activeCardLayout,
        })
          .where({
            programIncrementId: this.programIncrementId,
          })
          .per(100)
          .findInBatches({ concurrency: 2 });
      })
    );

    this.local.records = records.flat();

    return this.local.records;
  }

  /**
   * Build the default record scope to load all workable items for this team/iteration.
   */
  static buildDefaultRecordScope(
    options: { showCollapsedView?: boolean; cardLayout?: CardLayout } = {}
  ) {
    const scopes = BookmarksProgramBoard.OBJECT_CLASSES.map(objectClass =>
      this.buildRecordScope(objectClass, options)
    ).reduce((acc, scope) => acc.union(scope));

    return scopes;
  }

  /**
   * Return a scope that can be used to find records belonging to a program board.
   */
  static buildRecordScope(
    objectClass,
    options: { showCollapsedView?: boolean; cardLayout?: CardLayout } = {}
  ) {
    const { cardLayout } = options;
    const recordLayout = cardLayout?.recordLayout(objectClass.typename);

    const recordLinkUnion = BookmarksProgramBoard.OBJECT_CLASSES.map(
      objectClass => objectClass.select('id', 'referenceNum', 'path')
    ).reduce((acc, scope) => acc.union(scope));

    const includes = {
      teamWorkflowStatus: WorkflowStatus.select([
        'id',
        'name',
        'internalMeaning',
      ]).first(),
      assignedToUser: User.select([
        'id',
        'name',
        'avatarUrl',
        'initials',
      ]).first(),
      team: ['id'],
      iteration: ['id'],
      bookmarksRecordPositions: this.recordPositionScope(),
      parentRecordLinks: RecordLink.select(
        'id',
        'linkType',
        'relationDescription',
        'inverted'
      )
        .merge({
          childRecord: recordLinkUnion,
        })
        .where({
          linkType: [
            aha.enums.RecordLinkTypeEnum.DEPENDS_ON.value,
            aha.enums.RecordLinkTypeEnum.IMPACTS.value,
            aha.enums.RecordLinkTypeEnum.BLOCKS.value,
          ],
          recordType: [
            aha.enums.LinkableRecordTypesEnum.Feature.value,
            aha.enums.LinkableRecordTypesEnum.Requirement.value,
            aha.enums.LinkableRecordTypesEnum.Epic.value,
          ],
        }),
    };

    if (objectClass.hasField('epic')) {
      includes.epic = Epic.select(COMMON_FIELDS).first();
    }

    if (objectClass.hasField('feature')) {
      includes.feature = Feature.select(COMMON_FIELDS).first();
    }

    if (objectClass.hasField('release')) {
      includes.release = BACKLOG_RELEASE_SCOPE;
    }

    if (objectClass.hasField('project')) {
      includes.project = Project.select(['id', 'name', 'isTeam'])
        .first()
        .merge({
          projectConfiguration: ProjectConfiguration.merge({
            projectRisksConfiguration: ['enabled', 'manuallyFlagged'],
          }),
        });
    }

    if (objectClass.hasField('tags')) {
      includes.tags = ['id', 'name', 'color'];
    }

    let query = objectClass.select(COMMON_FIELDS).merge(includes);

    if (recordLayout) {
      const cardView = options.showCollapsedView
        ? recordLayout.collapsed
        : recordLayout.expanded;
      query = cardView.buildScope(query);
    }

    return query;
  }

  static recordPositionScope() {
    return BookmarksRecordPosition.select([
      'id',
      'position',
      'bookmarkType',
      'secondaryScope',
    ]).where({
      bookmarkType: [BookmarksTeamBacklog.bookmarkName, 'Iteration'],
    });
  }

  get activeCardLayout() {
    const { cardLayout, defaultCardLayout } = this;

    if (cardLayout && defaultCardLayout) {
      return CardLayout.buildActiveCardLayout({
        cardLayout,
        defaultCardLayout,
      });
    }
  }

  /**
   * `true` if the record can show on this board, 'false' otherwise.
   */
  canContainRecord(record) {
    return (
      record.iteration?.id &&
      this.iterations.map(i => i.id).includes(record.iteration.id)
    );
  }

  canFilter(scope) {
    return !!this.getFilter(scope);
  }

  /**
   * Returns a Filter subclass initialized with the active set of
   * filters. This is used, for example, to fetch user names when
   * there are active user filters, so we can display them in the
   * dropdown.
   *
   * @param filterScope The key for the filter hash stored in the workflow board config. For example, "users" or "epics"
   * @param recordScope A Query used to load filter information from the server
   * @param FilterClass The Filter subclass to initialize with the retrieved data
   * @param extraRecords Any extra records to append to the list. "Unassigned," for example.
   */
  async loadFilter(
    filterScope: string,
    recordScope: Query<any, any>,
    FilterClass: typeof RecordFilter,
    extraRecords: any[] = []
  ) {
    const filteredIds = this.filters[filterScope] || [];
    let filteredRecords = [];

    if (filteredIds.length > 0) {
      filteredRecords = await ApplicationModel.fetchAll(
        recordScope.where({
          id: filteredIds,
        })
      );
    }

    const filteredOptions = filteredRecords
      .concat(extraRecords)
      .map(t => [t.name, t.id || ''] as [string, RecordId]);

    return new FilterClass(filteredIds, filteredOptions);
  }

  /**
   * Loads filter data for all possible filter types.
   */
  async createFilters() {
    const tagFilterPromise = this.loadFilter(
      'tags',
      Tag.select(['id', 'name']),
      TagFilter,
      []
    );
    const teamsFilterPromise = this.loadFilter(
      'team',
      Project.select(['id', 'name']),
      TeamFilter,
      []
    );
    const projectsFilterPromise = this.loadFilter(
      'project',
      Project.select(['id', 'name']),
      ProjectFilter,
      []
    );
    const releasesFilterPromise = this.loadFilter(
      'release',
      Release.select(['id', 'name']),
      ReleaseFilter,
      []
    );
    const recordType = new RecordTypeFilter({
      initialItems: this.filters?.record_type,
      recordTypes: ['Feature', 'Requirement', 'Epic'],
    });

    const [tagFilter, teamsFilter, projectsFilter, releaseFilter] =
      await Promise.all([
        tagFilterPromise,
        teamsFilterPromise,
        projectsFilterPromise,
        releasesFilterPromise,
      ]);

    return {
      tags: tagFilter,
      record_type: recordType,
      team: teamsFilter,
      project: projectsFilter,
      release: releaseFilter,
    };
  }

  /**
   * Updates the server-side board state with the current filter state.
   */
  writeFilterAttribute(_scope) {
    this.clearUnfiltered();
  }

  canDeleteFilterItem(scope) {
    // @ts-ignore filters are untyped
    return this.getFilter(scope).hasItems;
  }

  /**
   * Returns a list of Filter objects that can be used on the active
   * board. This excludes filters that don't make sense to run -- `epic`
   * on the Epics board, for example.
   */
  get activeFilters() {
    return Object.entries(
      this.local.filterObjects as Record<string, RecordFilter>
    )
      .filter(([key, value]) => this.canFilter(key) && value.hasValue)
      .map(([key, value]) => value);
  }

  /**
   * Returns any `records` matched by all active filters.
   */
  applyFilters(records) {
    return records.filter(record => this.isRecordVisible(record));
  }

  /**
   * Returns any `records` matched by `filter`.
   *
   * Will ignore any record marked as "unfiltered", to prevent new
   * records that don't match the active filters from immediately
   * disappearing.
   */
  applyFilter(filter, records) {
    return records.filter(rec => this.unfiltered(rec) || filter.matches(rec));
  }

  /**
   * Returns whether the record is visible (not filtered) on the board or not.
   */
  isRecordVisible(record) {
    const { activeFilters } = this;

    return (
      this.unfiltered(record) ||
      activeFilters.every(filter => filter.matches(record))
    );
  }

  /**
   * Returns `true` if a record is considered `unfilterable`, false otherwise.
   *
   * When adding a record to a board, it may not match the active set
   * of filters -- for example, if you're filtering by tags, the
   * record will not have the active tags. This means the record would immediately disappear.
   *
   * By flagging a record as `unfilterable`, the record will always
   * appear until the board state changes.
   */
  unfiltered(record) {
    return (
      record.id === undefined || this.local.unfilteredRecordIds.has(record.id)
    );
  }

  /**
   * Flag `record` as unfilterable.
   */
  unfilter(record) {
    if (!this.activeFilters.every(f => f.matches(record))) {
      this.local.unfilteredRecordIds.add(record.id);
    }
  }

  /**
   * Resets the `unfilterable` flag on all records.
   */
  clearUnfiltered() {
    this.local.unfilteredRecordIds.clear();
  }
}

export default BookmarksProgramBoard;
